import { styled } from '@mui/material/styles';
import { Box, Typography, IconButton, TextField } from '@mui/material';

// Base components
export const SideDrawer = styled(Box)`
  & .MuiDrawer-paper {
    width: 280px;
    background-color: ${({ theme }) => theme.palette.background.paper};
    border-right: 1px solid ${({ theme }) => theme.palette.divider};
    padding: 24px 0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
  }
`;

export const MainContainer = styled(Box)`
  display: flex;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.palette.background.default};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const ChatContainer = styled(Box)`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-width: 1150px;
  margin-left: 100px;
  margin-right: ${({ theme }) => theme.spacing(4)};
  position: relative;
  background-color: ${({ theme }) => theme.palette.background.default};
  
  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin-left: 0;
    margin-right: 0;
  }
`;

export const MessageList = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(2)};
  padding-right: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;

  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.palette.divider};
    border-radius: 2px;
  }
`;

export const MessageContainer = styled(Box)`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  background-color: ${({ theme }) => 
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
  };
  width: 100%;
  max-width: 100%;
  margin-right: ${({ theme }) => theme.spacing(1)};
  
  &:hover {
    background-color: ${({ theme }) => 
      theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
    };
  }
`;

export const MessageItem = styled(Box)`
  padding: ${({ theme }) => theme.spacing(3, 3.5)};
  background-color: ${({ isUser, theme }) => isUser ? theme.palette.background.elevated : theme.palette.background.paper};
  border-radius: ${({ theme }) => theme.spacing(2.5)};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid ${({ theme }) => theme.palette.divider};
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }
  
  & .message-content {
    max-width: 100%;
    margin: 0 auto;
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: ${({ theme }) => theme.typography.body1.fontSize};
    line-height: 1.7;
    letter-spacing: 0.01em;
    position: relative;
    padding-left: ${({ theme }) => theme.spacing(4)};
    
    ${({ theme }) => theme.breakpoints.up('sm')} {
      font-size: ${({ theme }) => theme.typography.body1.fontSize};
    }
    
    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: calc(${({ theme }) => theme.typography.body1.fontSize} * 1.1);
    }
    
    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 2px;
      width: 4px;
      height: 24px;
      background-color: ${({ isUser, theme }) => isUser ? theme.palette.secondary.main : theme.palette.primary.main};
      border-radius: 4px;
      opacity: 0.8;
    }
  }
`;

export const InputContainer = styled(Box)`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${({ theme }) => theme.spacing(2)};
  background-color: ${({ theme }) => theme.palette.background.default};
  border-top: 1px solid ${({ theme }) => theme.palette.divider};
  margin-top: auto;
  width: 100%;

  & > div {
    max-width: 100%;
    margin: 0;
    padding: 0;
    display: flex;
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

export const StyledTextarea = styled('textarea')`
  width: 100%;
  min-height: 48px;
  max-height: 200px;
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  background-color: ${({ theme }) => theme.palette.background.paper};
  color: ${({ theme }) => theme.palette.text.primary};
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.5;
  resize: none;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${({ theme }) => theme.palette.primary.main};
  }

  &::placeholder {
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`;

export const StyledTextField = styled(TextField)`
  & .MuiInputBase-root {
    background-color: ${({ theme }) => theme.palette.background.paper};
    border-radius: 16px;
    padding: 14px 20px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid ${({ theme }) => theme.palette.divider};
    &:hover {
      background-color: ${({ theme }) => theme.palette.background.elevated};
      border-color: rgba(99, 102, 241, 0.3);
    }
    &.Mui-focused {
      background-color: ${({ theme }) => theme.palette.background.elevated};
      border-color: ${({ theme }) => theme.palette.primary.main};
      box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
    }
  }
  & .MuiInputBase-input {
    font-size: 16px;
    line-height: 1.6;
    color: ${({ theme }) => theme.palette.text.primary};
    &::placeholder {
      color: ${({ theme }) => theme.palette.text.secondary};
      opacity: 0.7;
    }
  }
`;

export const SendButton = styled(IconButton)`
  position: absolute;
  right: 12px;
  bottom: 12px;
  color: #6366F1;
  transition: all 0.2s ease;
  &:hover {
    color: #818CF8;
    transform: scale(1.1);
  }
  &.Mui-disabled {
    color: #6B7280;
  }
`;

export const PromptTitle = styled(Box)`
  padding: 8px 16px;
  color: #9CA3AF;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const CustomPromptTitle = styled(Box)`
  padding: 8px 16px;
  cursor: pointer;
  border-radius: 8px;
  margin-bottom: 4px;
  transition: all 0.2s ease;
  &:hover {
    background-color: rgba(55, 65, 81, 0.7);
  }
`;

export const PromptList = styled(Box)`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #4B5563;
    border-radius: 3px;
    &:hover {
      background: #6B7280;
    }
  }
`;

export const RoleListItem = styled(Box)`
  padding: 12px 16px;
  cursor: pointer;
  background-color: ${({ selected }) => selected ? 'rgba(55, 65, 81, 0.7)' : 'transparent'};
  color: #F9FAFB;
  border-radius: 8px;
  margin-bottom: 4px;
  transition: all 0.2s ease;
  &:hover {
    background-color: rgba(55, 65, 81, 0.7);
  }
`;

export const PromptCard = styled(Box)`
  background-color: rgba(45, 55, 72, 0.4);
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing(2.5)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, rgba(99, 102, 241, 0.2) 0%, rgba(99, 102, 241, 0.4) 100%);
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    background-color: rgba(45, 55, 72, 0.6);
    border: 1px solid rgba(99, 102, 241, 0.3);
    &::before {
      opacity: 1;
    }
  }
`;

export const PromptIcon = styled(Box)`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background-color: rgba(99, 102, 241, 0.1);
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

export const PromptTitleTypography = styled(Typography)`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

export const PromptTooltipTypography = styled(Typography)`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.palette.text.secondary};
  line-height: 1.5;
`;

export const CustomButton = styled(IconButton)`
  color: #6B7280;
  transition: all 0.2s ease;
  &:hover {
    color: #818CF8;
    transform: scale(1.1);
  }
  &.Mui-disabled {
    color: #6B7280;
  }
`;

export const PromptsSection = styled(Box)`
  padding: ${({ theme }) => theme.spacing(2)};
  height: 100%;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.palette.background.default};
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.palette.divider};
    border-radius: 2px;
  }
`;

export const PromptsSectionTitle = styled(Typography)`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const PromptsGrid = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

export const PromptCardNew = styled(Box)`
  position: relative;
  padding: ${({ theme }) => theme.spacing(1.5)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  background-color: ${({ theme }) => theme.palette.background.paper};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  transition: all 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }
`;

export const PromptCardContent = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const PromptIconNew = styled(Box)`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.palette.primary.main};
  flex-shrink: 0;
`;

export const PromptTextContent = styled(Box)`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const PromptTitleTypographyNew = styled(Typography)`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PromptTooltipTypographyNew = styled(Typography)`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.palette.text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FavoriteButton = styled(IconButton)`
  padding: ${({ theme }) => theme.spacing(0.5)};
  color: ${({ isFavorite, theme }) => isFavorite ? theme.palette.primary.main : theme.palette.action.active};
  margin-left: ${({ theme }) => theme.spacing(0.5)};
  
  &:hover {
    background-color: transparent;
    color: ${({ theme }) => theme.palette.primary.main};
  }
  
  & .MuiSvgIcon-root {
    font-size: 1rem;
  }
`;
